import { z } from 'zod';

import { isObj, isPlainObj } from '../utils';
import { SMALL_STRING_LIMIT } from './constants';
import { Document, zDocument, zDocuments } from './document';
import {
  JSONValue,
  jsonValueIsEmpty,
  jsonValuePreview,
  zJSON,
  zJSONArray,
  zJSONObject,
} from './json';
import { Message, zMessage, zMessages } from './message';

type WithPath<A> = { path: string[]; value: A };

export type SimplePrimitive = string | number | boolean | null;
export type FlatRecord = Record<string, SimplePrimitive>;

export type _JSON = {
  type: 'JSON';
  value: JSONValue;
  raw: unknown;
  path: string[];
};

export type _JSONArray = {
  type: 'JSONArray';
  value: Array<JSONValue>;
  raw: unknown;
  path: string[];
};

export type _JSONObject = {
  type: 'JSONObject';
  value: Record<string, JSONValue>;
  raw: unknown;
  path: string[];
};

export type _FlatRecord = {
  type: 'FlatRecord';
  value: FlatRecord;
  raw: unknown;
  path: string[];
};

export type _Message = {
  type: 'Message';
  value: Message;
  raw: unknown;
  path: string[];
};

export type _Messages = {
  type: 'Messages';
  value: Array<Message>;
  raw: unknown;
  path: string[];
};

export type _Document = {
  type: 'Document';
  value: Document;
  raw: unknown;
  path: string[];
};

export type _Documents = {
  type: 'Documents';
  value: Array<Document>;
  raw: unknown;
  path: string[];
};

export type _Unknown = {
  type: 'Unknown';
  value: unknown;
  raw: unknown;
  path: string[];
};

export type ForeignData =
  | _JSONArray
  | _JSONObject
  | _JSON
  | _FlatRecord
  | _Message
  | _Messages
  | _Document
  | _Documents
  | _Unknown;

export type ForeignDataTree =
  | { type: 'Leaf'; value: ForeignData; key: string; raw: unknown }
  | {
      type: 'Node';
      children: Array<ForeignDataTree>;
      key: string;
      raw: unknown;
    };

const zWithPath = <T extends z.ZodTypeAny>(value: T) =>
  z.object({ path: z.array(z.string()), value });

const zSimplePrimitive = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);

const zFlatRecord = z.record(zSimplePrimitive);

export const zAnyMessages = z.preprocess(
  (input) => {
    const seen = new Set<unknown>(); // cycle detection
    const go = (
      x: unknown,
      path: string[]
    ): null | WithPath<Message | Array<Message>> => {
      if (seen.has(x)) return null;
      seen.add(x);
      if (isObj(x)) {
        const keys = Object.keys(x);
        if (keys.length === 1) {
          const key = keys[0]!;
          const parsed = z.union([zMessage, zMessages]).safeParse(x[key]);
          if (parsed.success) {
            return { path: [...path, key], value: parsed.data };
          }
          return go(x[key], [...path, key]);
        }
      }
      return null;
    };
    return go(input, []);
  },
  zWithPath(
    z.union([
      zMessage.transform((value) => ({ type: 'Message' as const, value })),
      zMessages.transform((value) => ({ type: 'Messages' as const, value })),
    ])
  ).transform((x) => {
    return { ...x.value, path: x.path };
  })
);

export const zAnyDocument = z.preprocess(
  (input) => {
    const seen = new Set<unknown>(); // cycle detection
    const go = (
      x: unknown,
      path: string[]
    ): null | WithPath<Document | Array<Document>> => {
      if (seen.has(x)) return null;
      seen.add(x);
      if (isObj(x)) {
        const keys = Object.keys(x);
        if (keys.length === 1) {
          const key = keys[0]!;
          const parsed = z.union([zDocument, zDocuments]).safeParse(x[key]);
          if (parsed.success) {
            return { path: [...path, key], value: parsed.data };
          }
          return go(x[key], [...path, key]);
        }
      }
      return null;
    };
    return go(input, []);
  },
  zWithPath(
    z.union([
      zDocument.transform((value) => ({ type: 'Document' as const, value })),
      zDocuments.transform((value) => ({ type: 'Documents' as const, value })),
    ])
  ).transform((x) => {
    return { ...x.value, path: x.path };
  })
);

const zPartialForeignData = z.union([
  zMessage.transform((value) => ({ type: 'Message' as const, value })),
  zMessages.transform((value) => ({ type: 'Messages' as const, value })),
  zDocument.transform((value) => ({ type: 'Document' as const, value })),
  zDocuments.transform((value) => ({ type: 'Documents' as const, value })),
  zAnyMessages,
  zAnyDocument,
  zFlatRecord.transform((value) => ({ type: 'FlatRecord' as const, value })),
  zJSONArray.transform((value) => ({ type: 'JSONArray' as const, value })),
  zJSONObject.transform((value) => ({ type: 'JSONObject' as const, value })),
  zJSON.transform((value) => ({ type: 'JSON' as const, value })),
]);

// Unlike zPartialForeignData, we don't attempt to find nested messages / documents
// and flatten the structure as a result. This is useful when we want to just render
// the JSON structure nicely
const zSimpleForeignData = z.union([
  zMessage.transform((value) => ({ type: 'Message' as const, value })),
  zMessages.transform((value) => ({ type: 'Messages' as const, value })),
  zDocument.transform((value) => ({ type: 'Document' as const, value })),
  zDocuments.transform((value) => ({ type: 'Documents' as const, value })),
  zFlatRecord.transform((value) => ({ type: 'FlatRecord' as const, value })),
  zJSONArray.transform((value) => ({ type: 'JSONArray' as const, value })),
  zJSONObject.transform((value) => ({ type: 'JSONObject' as const, value })),
  zJSON.transform((value) => ({ type: 'JSON' as const, value })),
]);

export const parseForeignData = (
  data: unknown,
  path: string[] = [],
  variant: 'partial' | 'simple' = 'partial'
): ForeignData => {
  const parsed = (
    variant === 'partial' ? zPartialForeignData : zSimpleForeignData
  ).safeParse(data);
  if (parsed.success) {
    return {
      ...parsed.data,
      raw: data,
      path: [...path, ...(parsed.data['path'] ?? [])],
    };
  }
  return { type: 'Unknown', value: data, raw: data, path };
};

export const foreignDataIsEmpty = (data: ForeignData): boolean => {
  switch (data.type) {
    case 'FlatRecord':
      return Object.keys(data.value).length === 0;
    case 'Message':
      return data.value.content === '';
    case 'Messages':
      return data.value.length === 0;
    case 'Document':
      return data.value.page_content === '';
    case 'Documents':
      return data.value.length === 0;
    case 'JSON':
      return jsonValueIsEmpty(data.value);
    case 'JSONArray':
      return data.value.length === 0;
    case 'JSONObject':
      return Object.keys(data.value).length === 0;
    case 'Unknown':
      return false;
  }
};

export const foreignDataPreview = (data: ForeignData): string => {
  switch (data.type) {
    case 'FlatRecord':
      return Object.keys(data.value).length === 0 ? '{}' : '{...}';
    case 'Message':
      return data.value.content.length === 0
        ? '""'
        : data.value.content.slice(0, SMALL_STRING_LIMIT) + '...';
    case 'Messages':
      return data.value.length === 0 ? '[]' : '[...]';
    case 'Document':
      return data.value.page_content.length === 0
        ? '""'
        : data.value.page_content.slice(0, SMALL_STRING_LIMIT) + '...';
    case 'Documents':
      return data.value.length === 0 ? '[]' : '[...]';
    case 'JSON':
      return jsonValuePreview(data.value);
    case 'JSONArray':
      return data.value.length === 0 ? '[]' : '[...]';
    case 'JSONObject':
      return Object.keys(data.value).length === 0 ? '{}' : '{...}';
    case 'Unknown':
      return 'unknown';
  }
};

export const foreignDataToTree = ({
  data,
  parentKey = '',
  filterFn,
  messagesKey,
}: {
  data: ForeignData;
  parentKey?: string;
  filterFn?: (key: string) => boolean;
  messagesKey?: string;
}): ForeignDataTree => {
  if (
    data.type === 'JSON' ||
    data.type === 'JSONArray' ||
    data.type === 'JSONObject' ||
    data.type === 'FlatRecord'
  ) {
    const entries: Array<[string, unknown]> = Array.isArray(data.value)
      ? data.value.map((v, i) => [i.toString(), v])
      : isPlainObj(data.value)
      ? Object.entries(data.value)
      : [];

    const filteredEntries = filterFn
      ? entries.filter(([k]) => filterFn(k))
      : entries;

    // put strings first if not an array
    if (!Array.isArray(data.value)) {
      filteredEntries.sort(([k1, v1], [k2, v2]) => {
        if (messagesKey) {
          if (k1 === messagesKey) return -1;
          if (k2 === messagesKey) return 1;
        }
        if (typeof v1 === 'string' && typeof v2 !== 'string') {
          return -1;
        }
        if (typeof v1 !== 'string' && typeof v2 === 'string') {
          return 1;
        }
        return k1.localeCompare(k2);
      });
    }

    if (filteredEntries.length === 0) {
      return { type: 'Leaf', value: data, key: parentKey, raw: data.raw };
    }

    return {
      type: 'Node',
      children: filteredEntries.map(([k, v]) =>
        foreignDataToTree({
          data: parseForeignData(v, [...data.path, k]),
          parentKey: k,
          filterFn,
        })
      ),
      key: parentKey,
      raw: data.raw,
    };
  }

  return {
    type: 'Leaf',
    value: data,
    key: parentKey,
    raw: data.raw,
  };
};
